// src/components/TradeRow.jsx
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiUrl } from "../config/api";
import { adminAuthHeaders } from "../utils/authHeaders";

function TradeRow({ trade, onStatusChange }) {
  const [timeLeft, setTimeLeft] = useState(trade.remainingTime || 80);
  const [isProcessing, setIsProcessing] = useState(false);

  const formatNumber = (num) => {
    if (!num) return "-";
    return Number(num).toLocaleString("fa-IR");
  };

  const getTradeTypeText = (mode) => (mode === "buy" ? "خرید از ما" : "فروش به ما");

  const getTradeStatusText = (status) => {
    switch (status) {
      case "pending":
        return "در انتظار";
      case "approved":
        return "تایید شده";
      case "rejected":
        return "رد شده";
      default:
        return status;
    }
  };

  const handleReject = useCallback(
    async (id) => {
      setIsProcessing(true);
      try {
        const response = await fetch(apiUrl(`/api/trades/${id}/reject`), {
          method: "POST",
          headers: adminAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ reason: "رد شده توسط مدیر" }),
        });

        const result = await response.json();
        if (result.success) {
          toast.info("معامله رد شد.");
          onStatusChange(id, "rejected");
        } else {
          toast.error(result.message || "خطا در رد معامله");
        }
      } catch (error) {
        toast.error("خطا در ارتباط با سرور");
      } finally {
        setIsProcessing(false);
      }
    },
    [onStatusChange]
  );

  useEffect(() => {
    if (trade.status !== "pending") return;

    const expiresAt = new Date(trade.expiresAt).getTime();

    const timer = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        handleReject(trade.id);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [handleReject, trade.expiresAt, trade.id, trade.status]);

  const handleApprove = async (id) => {
    if (!window.confirm("آیا از تایید این معامله اطمینان دارید؟")) return;

    setIsProcessing(true);
    try {
      const response = await fetch(apiUrl(`/api/trades/${id}/approve`), {
        method: "POST",
        headers: adminAuthHeaders({ "Content-Type": "application/json" }),
      });

      const result = await response.json();
      if (result.success) {
        toast.success("معامله با موفقیت تایید شد.");
        onStatusChange(id, "approved");
      } else {
        toast.error(result.message || "خطا در تایید");
      }
    } catch (error) {
      toast.error("خطا در ارتباط با سرور");
    } finally {
      setIsProcessing(false);
    }
  };

  const isFinalStatus = trade.status !== "pending";
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (timeLeft / 80) * circumference;
  const baseRate = trade.mode === "buy" ? trade.valueBuy : trade.valueSell;

  return (
    <div className="DealRow mt-3 grid grid-cols-8 gap-2 p-2 rounded">
      <p className="truncate" title={trade.userName}>
        {trade.userName || "کاربر"}
      </p>
      <p className="truncate">{trade.itemId || "-"}</p>
      <p className={`p-1 rounded text-xs ${trade.mode === "buy" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
        {getTradeTypeText(trade.mode)}
      </p>
      <p className={`p-1 rounded text-xs ${trade.status === "pending" ? "bg-yellow-100 text-yellow-800" : "bg-gray-200 text-gray-600"}`}>
        {getTradeStatusText(trade.status)}
      </p>
      <p className="font-mono text-sm">{formatNumber(baseRate)}</p>
      <p className="font-mono text-sm">{formatNumber(trade.totalPrice)}</p>
      <p className="font-mono text-sm">{formatNumber(trade.weight)} گرم</p>

      <div className="relative flex items-center justify-center">
        {trade.status === "pending" ? (
          <>
            <svg className="w-12 h-12 transform -rotate-90">
              <circle cx="24" cy="24" r={radius} className="text-gray-200" strokeWidth="4" fill="transparent" />
              <circle
                cx="24"
                cy="24"
                r={radius}
                className={`transition-all duration-1000 ease-linear ${timeLeft <= 10 ? "text-red-500" : "text-blue-500"}`}
                strokeWidth="4"
                fill="transparent"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute text-xs font-bold text-gray-700">{timeLeft}</span>
          </>
        ) : (
          <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
            trade.status === "approved" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
          }`}>
            {trade.status === "approved" ? "✓" : "×"}
          </div>
        )}
      </div>

      <div className="flex gap-1">
        <button
          onClick={() => handleApprove(trade.id)}
          disabled={isFinalStatus || isProcessing}
          className="AgreeUser"
        >
          تایید
        </button>
        <button
          onClick={() => handleReject(trade.id)}
          disabled={isFinalStatus || isProcessing}
          className="EgnorUser"
        >
          رد
        </button>
      </div>
    </div>
  );
}

export default TradeRow;
